import config from '../models.json'

// The model lineup lives in src/models.json. Each entry becomes a builtin AI
// user (synced to Mongo by server/sync_models.ts) and drives routing, limits
// and pricing on the server. Roles pick the model behind defaults and the
// builtin tools; `doc` and `web` must be Anthropic models (see server/claude.ts).
export interface ModelConfig {
  id: string // builtin user id; `model` with dots swapped out, since ids are Mongo map keys
  model: string // `<provider>/<api name>`, or the full OpenRouter id
  name: string
  subtitle: string
  description: string
  provider: 'anthropic' | 'openai' | 'xai' | 'openrouter'
  pinned?: boolean
  premium?: boolean
  vision?: boolean
  reasoning?: boolean // OpenAI Responses API with reasoning effort
  context: [number, number] // input tokens we send: [standard, pro plan]
  max_input: number // provider context window, used with Flex full context
  max_output?: number
  price: [number, number] // USD per million tokens: [input, output]
  order: number
}

export const MODELS: ModelConfig[] = config.models.map(
  (m, order) =>
    ({ ...m, id: m.model.replace(/\./g, '-'), order }) as ModelConfig,
)

export const MODEL_ROLES: Record<keyof typeof config.roles, string> =
  config.roles

const aliases: Record<string, string> = config.aliases
const by_key: Record<string, ModelConfig> = Object.fromEntries(
  MODELS.flatMap((m) => [
    [m.id, m],
    [m.model, m],
  ]),
)

// Any model string we have ever stored (builtin id, api name, retired model)
// to the api model we serve today. Unknown strings pass through to OpenRouter.
export function resolve_model(model: string) {
  const current = aliases[model] ?? model
  return by_key[current]?.model ?? current
}

export function model_config(model: string): ModelConfig | undefined {
  return model ? by_key[resolve_model(model)] : undefined
}

export function is_vision_model(model: string) {
  return !!model_config(model)?.vision
}

export function is_reasoning_model(model: string) {
  return !!model_config(model)?.reasoning
}
